import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Google,
  LocationOnOutlined,
  LogoutOutlined,
  PersonOutline,
  PhoneOutlined,
  PhotoCamera,
  SecurityOutlined,
  Brightness4Outlined,
  Brightness7Outlined,
  SettingsBrightnessOutlined,
} from '@mui/icons-material';
import { CWY_COLORS } from '../../theme/colors';
import { ACCOUNT_COLORS } from './accountPageStyles';
import { AccountButton } from './AccountButton';
import { formatClientPhone, getClientLocationLine } from '../../utils/clientProfileDisplay';
import type { Client } from '../../services/authService';
import { useAppTheme } from '../../contexts/ThemeContext';
import { MembershipAvatar } from '../../features/membership/MembershipAvatar';
import { MembershipTierBadge } from '../../features/membership/MembershipTierBadge';
import { useClientMembership } from '../../features/membership/useClientMembership';
import { getMembershipTierChrome } from '../../features/membership/membershipTierChrome';

interface AccountProfileBandProps {
  client: Client | null;
  dpLoading: boolean;
  dpError: string;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearDpError: () => void;
  onEditName: () => void;
  onEditPhone: () => void;
  onEditLocation: () => void;
  onEditSecurity: () => void;
  onLogout: () => void;
  logoutLoading?: boolean;
}

export const AccountProfileBand: React.FC<AccountProfileBandProps> = ({
  client,
  dpLoading,
  dpError,
  onFileChange,
  onClearDpError,
  onEditName,
  onEditPhone,
  onEditLocation,
  onEditSecurity,
  onLogout,
  logoutLoading,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadStartedRef = useRef(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);

  useEffect(() => {
    if (uploadStartedRef.current && !dpLoading) {
      setPhotoModalOpen(false);
      uploadStartedRef.current = false;
    }
  }, [dpLoading]);
  const phoneLabel = formatClientPhone(
    client?.phone,
    client?.country_code ?? '1',
    client?.address?.country
  );
  const locationLabel = getClientLocationLine(client);

  const { mode, setMode } = useAppTheme();
  const { isLive, membership } = useClientMembership({ syncFromServer: true });
  const tierChrome = isLive ? getMembershipTierChrome(membership.tier) : null;

  const handleToggleTheme = () => {
    if (mode === 'system') setMode('dark');
    else if (mode === 'dark') setMode('light');
    else setMode('system');
  };

  const getThemeIcon = () => {
    if (mode === 'dark') return <Brightness4Outlined fontSize="small" />;
    if (mode === 'light') return <Brightness7Outlined fontSize="small" />;
    return <SettingsBrightnessOutlined fontSize="small" />;
  };

  const getThemeValue = () => {
    if (mode === 'dark') return 'Dark mode';
    if (mode === 'light') return 'Light mode';
    return 'System default';
  };

  const rows = [
    {
      key: 'name',
      icon: <PersonOutline fontSize="small" />,
      label: 'Display name',
      value: client?.name || 'Not set',
      onClick: onEditName,
    },
    {
      key: 'phone',
      icon: <PhoneOutlined fontSize="small" />,
      label: 'Phone',
      value: phoneLabel || 'Not set',
      onClick: onEditPhone,
    },
    {
      key: 'location',
      icon: <LocationOnOutlined fontSize="small" />,
      label: 'Location',
      value: locationLabel || 'Not set',
      onClick: onEditLocation,
    },
    {
      key: 'security',
      icon: <SecurityOutlined fontSize="small" />,
      label: 'Password & security',
      value: client?.has_password ? 'Password set' : client?.has_google ? 'Google sign-in' : 'Not configured',
      onClick: onEditSecurity,
    },
    {
      key: 'appearance',
      icon: getThemeIcon(),
      label: 'Appearance',
      value: getThemeValue(),
      onClick: handleToggleTheme,
    },
  ];

  return (
    <Box sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
        <Box
          onClick={() => setPhotoModalOpen(true)}
          role="button"
          tabIndex={0}
          aria-label="View profile photo"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPhotoModalOpen(true);
            }
          }}
          sx={{
            position: 'relative',
            display: 'inline-block',
            mb: 2,
            cursor: 'pointer',
            borderRadius: '50%',
            lineHeight: 0,
            '&:hover .profile-avatar': { opacity: 0.9 },
          }}
        >
          <MembershipAvatar
            className="profile-avatar"
            name={client?.name}
            src={client?.profile_picture}
            tier={membership.tier}
            showRing={isLive}
            size="lg"
          />
        </Box>
        {tierChrome && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <MembershipTierBadge tier={tierChrome.id} />
          </Box>
        )}

        <Typography sx={{ fontWeight: 600, fontSize: '1.125rem', color: CWY_COLORS.textPrimary }}>
          {client?.name || 'Member'}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: CWY_COLORS.textSecondary, mt: 0.5 }}>
          {client?.email || 'No email on file'}
        </Typography>
        {client?.has_google && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.25 }}>
            <Chip
              size="small"
              icon={<Google sx={{ fontSize: 14, color: '#4285F4 !important' }} />}
              label="Google"
              sx={{ height: 24, fontSize: '0.75rem' }}
            />
          </Box>
        )}
      </Box>

      {dpError && (
        <Alert severity="error" sx={{ mb: 2, mt: 2, width: '100%', maxWidth: 360 }} onClose={onClearDpError}>
          {dpError}
        </Alert>
      )}

      <Divider sx={{ my: 2, width: '100%', maxWidth: 360 }} />

      <List disablePadding sx={{ width: '100%', maxWidth: 360 }}>
        {rows.map((row) => (
          <ListItemButton
            key={row.key}
            onClick={row.onClick}
            sx={{
              px: 0,
              py: 1.25,
              borderBottom: `1px solid ${CWY_COLORS.border}`,
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: CWY_COLORS.textSecondary }}>{row.icon}</ListItemIcon>
            <ListItemText
              primary={row.label}
              secondary={row.value}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.8125rem', color: CWY_COLORS.textSecondary }}
            />
          </ListItemButton>
        ))}
        <ListItemButton
          onClick={onLogout}
          disabled={logoutLoading}
          sx={{ px: 0, py: 1.25, mt: 0.5, color: CWY_COLORS.priceRed }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
            <LogoutOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={logoutLoading ? 'Signing out…' : 'Sign out'}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
          />
        </ListItemButton>
      </List>

      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => {
          if (e.target.files?.[0]) uploadStartedRef.current = true;
          onFileChange(e);
        }}
        accept="image/*"
        style={{ display: 'none' }}
      />

      <Dialog
        open={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: ACCOUNT_COLORS.radius } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Profile photo
          <IconButton size="small" onClick={() => setPhotoModalOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 240,
              mb: 2.5,
              position: 'relative',
            }}
          >
            {dpLoading && (
              <CircularProgress
                size={40}
                sx={{ position: 'absolute', zIndex: 1, color: CWY_COLORS.red }}
              />
            )}
            {client?.profile_picture ? (
              <Box
                component="img"
                src={client.profile_picture}
                alt={client?.name || 'Profile photo'}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 320,
                  borderRadius: '8px',
                  objectFit: 'contain',
                  opacity: dpLoading ? 0.5 : 1,
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 160,
                  height: 160,
                  bgcolor: CWY_COLORS.red,
                  fontSize: '3rem',
                  opacity: dpLoading ? 0.5 : 1,
                }}
              >
                {client?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
            )}
          </Box>

          <Stack direction="row" spacing={1} justifyContent="center">
            <AccountButton
              accountVariant="gold"
              startIcon={<PhotoCamera sx={{ fontSize: 18 }} />}
              loading={dpLoading}
              onClick={() => fileInputRef.current?.click()}
            >
              Change photo
            </AccountButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
